import { useEffect, useState } from 'react';
import { space } from '@repo/design-tokens';

/**
 * The glass steps' spacing, in one place (`onboarding-persistent-scene-camera-feeds-glass`, from Rob's
 * Onboarding Glass Panels canvas, frame 00 "Edges"): the nav bar sits 8 px in from the top and
 * sides, and every panel shares that 8 px edge and sits 12 px below the bar and 12 px from its
 * neighbour. Every floating piece of a glass step (the step list, the list panel, the floor
 * selector, the legend, the tool rail and the camera card's home) reads its offset from here, so
 * the canvas's spacing is never restated as a literal beside it.
 *
 * Values are CSS lengths (strings), for styled-components and the planner's host-given slots.
 */

/** The 8 px edge every glass piece keeps from the window. */
export const GLASS_EDGE = space[2];

/** The 12 px gap below the bar and between neighbouring panels. */
export const GLASS_GAP = space[3];

/** The nav bar's own height (`NavBar.tsx`, `height: 52px`), which the design system holds as a literal. */
export const NAV_BAR_HEIGHT = '52px';

/** Where the nav bar ends: the header's 8 px margin (`OnboardingHeader.tsx`) plus the bar. */
export const BAR_BOTTOM = `calc(${space[2]} + ${NAV_BAR_HEIGHT})`;

/** Where a panel starts: 12 px below the bar. */
export const PANEL_TOP = `calc(${BAR_BOTTOM} + ${GLASS_GAP})`;

/** The step list's two widths (`OnboardingNav.tsx`): numbers only, and whole. */
export const NAV_WIDTH_COLLAPSED = '64px';
export const NAV_WIDTH_EXPANDED = '240px';

/**
 * The custom property the page sets on a glass step: the left edge of the first panel beside the
 * step list, which moves with the list's width. Consumers read it as `var(--onboarding-glass-start)`.
 */
export const GLASS_START_VAR = '--onboarding-glass-start';
export const GLASS_START = `var(${GLASS_START_VAR})`;

/** The first panel's left edge for a step list of `navWidth`. */
export const glassStartFor = (navWidth: string): string =>
  `calc(${GLASS_EDGE} + ${navWidth} + ${GLASS_GAP})`;

/** Camera Feeds' RTSP stream wall: 480 px, and 420 px at 1280 px wide or narrower (canvas, "At 1280"). */
export const WALL_WIDTH = '480px';
export const WALL_WIDTH_NARROW = '420px';
export const NARROW_VIEWPORT_QUERY = '(max-width: 1280px)';

/**
 * The editor steps' list panel (Floor Plan's lists; Observers' inventory): 360 px at every viewport
 * width (canvas frame 00, "List panel · 360"; "At 1280, panels keep their widths").
 */
export const LIST_PANEL_WIDTH = '360px';

/**
 * The right-hand panel beside the plan (Observers' likely cameras): 360 px at every viewport width
 * (canvas frame 00, "Inspector · 360"; `onboarding-persistent-scene-observers-glass` R3.1).
 */
export const INSPECTOR_WIDTH = '360px';

/**
 * Pixel values of the lengths above, for code that places by number (the camera card's home inset).
 * Derived from the same tokens, never typed twice.
 */
export const px = (length: string): number => parseInt(length, 10);
export const PANEL_TOP_PX = px(space[2]) + px(NAV_BAR_HEIGHT) + px(GLASS_GAP);
export const GLASS_EDGE_PX = px(GLASS_EDGE);
export const GLASS_GAP_PX = px(GLASS_GAP);

/**
 * What sits beside the camera card while it is open at its home corner (R4.1, R6.1–6.2): the right
 * inset that clears the card by `gaps` glass gaps (one for the tool rail, two for the floor
 * selector's span). A card moved away, closed or not yet measured leaves the 8 px edge.
 */
export const besideCardRight = (
  card: { open: boolean; atHome: boolean; width: number },
  gaps: number,
): string =>
  card.open && card.atHome && card.width > 0
    ? `${card.width + GLASS_EDGE_PX + gaps * GLASS_GAP_PX}px`
    : GLASS_EDGE;

/**
 * Whether a media query matches now, following changes (the 1280 px wall and its xs chips). False
 * where the window has no `matchMedia` (jsdom), so a test sees the wide layout unless it stubs one.
 */
export const useMatchesMedia = (query: string): boolean => {
  const read = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener?.('change', onChange);
    return () => list.removeEventListener?.('change', onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  return matches;
};
